import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';

interface ConditionalStateOptions<T> {
  /**
   * Callback function to be called when the state changes.
   */
  onChange?: (from: T, to: T) => void;

  /**
   * Callback function to be called when the state change is skipped.
   */
  onSkip?: () => void;
}

/**
 * Comparator type definition.
 * @template T
 * @param {T} prev - The previous state value.
 * @param {T} next - The next state value.
 * @returns {boolean} - A boolean indicating whether the state should be updated.
 */
type Comparator<T> = (prev: T, next: T) => boolean;

type OptionalRecord<K extends keyof any, T> = {
  [P in K]?: T;
};

/**
 * useConditionalState hook
 *
 * A custom hook that behaves like useState, but only updates the state if the provided comparator function returns true.
 *
 * @template T
 * @param {T} initialValue - The initial state value.
 * @param {Comparator<T>} didItChange - A comparator function that determines whether the state should be updated.
 * @returns {[T, Dispatch<SetStateAction<T>>]} - The current state and a function to update the state conditionally.
 */
export function useConditionalState<T>(
  initialValue: T,
  didItChange: Comparator<T>,
  { onChange, onSkip }: ConditionalStateOptions<T> = {}
): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(initialValue);

  const stableDidItChange = useRef(didItChange);
  const stableOnChange = useRef(onChange);
  const stableOnSkip = useRef(onSkip);

  const setConditionalState = useCallback((nextValue: SetStateAction<T>) => {
    setState(prevState => {
      const resolvedNextValue = nextValue instanceof Function ? nextValue(prevState) : nextValue;
      if (stableDidItChange.current(prevState, resolvedNextValue)) {
        stableOnChange.current instanceof Function &&
          stableOnChange.current(prevState, resolvedNextValue);
        return resolvedNextValue;
      }

      stableOnSkip.current instanceof Function && stableOnSkip.current();
      return prevState;
    });
  }, []);

  return [state, setConditionalState];
}

/**
 * Creates a comparator function to determine if two lists do not match based on
 * a specified key and order of items.
 *
 * @template Key - The type of the key used for comparison.
 * @template ListType - The type of the list items, which must extend a record
 * with the specified key.
 *
 * @param {Key} [key='_id'] - The key used to compare items in the lists.
 * @returns {Comparator<ListType[]>} - A comparator function that returns `true`
 * if the lists have changed.
 */
export function createListComparator<
  ListType extends OptionalRecord<Key, unknown>[],
  Key extends string = 'id'
>(keys: Key[] = ['id'] as Key[]): Comparator<ListType> {
  return function listComparator(prev: ListType, next: ListType): boolean {
    return (
      prev.length !== next.length ||
      prev.some((item, index) =>
        keys.some(key => {
          // Check if the key exists in one but not the other, consider it a difference
          const prevHasKey = key in item;
          const nextHasKey = key in next[index];

          if (prevHasKey !== nextHasKey) {
            return true;
          }

          // If the key exists in both, compare their values
          return prevHasKey && item[key] !== next[index][key];
        })
      )
    );
  };
}

/**
 * A comparator function to check if two lists do not match based on the '_id'
 * key and the order of items.
 *
 * @returns {boolean} - Returns `true` if the lists do not match.
 */
export const compareBy_id = createListComparator(['_id']);

/**
 * A comparator function to check if two lists do not match based on the '_id' and 'errors'
 * key and the order of items.
 *
 * @returns {boolean} - Returns `true` if the lists do not match.
 */
export const compareBy_idAndErrors = createListComparator(['_id', 'errors']);
